import React, { useState, useRef } from 'react';
import Modal from 'fast-modal-library';
import { useSelector } from 'react-redux';

/**
 * AdminCreateSignupPage est une page qui permet à un administrateur de créer une URL de création de compte.
 * L'administrateur entre le nom de l'entreprise et le SIRET, puis une URL est générée.
 * Cette URL est ensuite affichée à l'administrateur, qui peut la copier dans le presse-papiers.
 * L'URL contient le nom de l'entreprise et le SIRET chiffrés en AES et encodés pour être sûrs dans une URL.
 * Lorsqu'un utilisateur accède à cette URL, le nom de l'entreprise et le SIRET sont déchiffrés et utilisés pour remplir le formulaire de création de compte.
 * Cette page utilise la bibliothèque CryptoJS pour le chiffrement AES.
 * Cette page utilise le hook useState de React pour gérer l'état des champs de saisie et de l'URL générée.
 * Cette page utilise le hook useRef de React pour accéder au champ de saisie de l'URL et le sélectionner pour la copie.
 * Cette page utilise la fonction encodeURIComponent de JavaScript pour encoder les données dans l'URL.
 * Cette page utilise la méthode execCommand de JavaScript pour copier le contenu du champ de saisie dans le presse-papiers.
 * 
 *
 */
const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminCreateSignupPage = () => {
    const token = useSelector((state) => state.user.token);
    const Numberapp = useSelector((state) => state.app.appNumber);
    const [entreprise, setEntreprise] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const [siret, setSiret] = useState('');
    const [url, setUrl] = useState('');
    const [emails, setEmails] = useState('');

    const urlRef = useRef(null);

    const handleCopyUrl = () => {
        urlRef.current.select();
        document.execCommand('copy');
    };

    const hide = () => {
        setShowModal(false);
    };

    const handleCreateUrl = async () => {
        try {
            const response = await fetch(apiPrefix + 'signup-link', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    company: entreprise,
                    siret,
                    appNumber: Numberapp,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.url) {
                setMessage(data.error || 'Impossible de créer le lien');
                setShowModal(true);
                return;
            }
            setUrl(data.url);
        } catch (error) {
            console.error(error);
            setMessage('Impossible de créer le lien');
            setShowModal(true);
        }
    };
    
    
    const handleSendInvite = async () => {
        const emailList = emails.split(',').map(email => email.trim());
        try {
            const responses = await Promise.all(emailList.map(email => 
                fetch(apiPrefix + 'send-invite', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email,
                        url,
                        entreprise,
                        appNumber: Numberapp
                    }),
                })
            ));
    
            const allSuccessful = responses.every(response => response.ok);
    
            if (allSuccessful) {
                setMessage('Invitations envoyées avec succès');
                setShowModal(true);
            } else {
                setMessage('Erreur lors de l\'envoi des invitations');
                setShowModal(true);
            }
        } catch (error) {
            console.error(error);
        }
    };
    

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="card-theme mb-8">
                    <h1 className="text-4xl font-bold text-theme-primary mb-4">Créer une URL de création de compte</h1>
                    <p className="text-gray-600 text-lg">
                        Entrez le nom de l'entreprise et le SIRET pour créer une URL de création de compte
                    </p>
                </div>

                <div className="card-theme mb-8">
                    <h2 className="text-2xl font-bold text-theme-primary mb-6">Informations de l'entreprise</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-building mr-2 text-theme-accent"></i>
                                Nom de l'entreprise
                            </label>
                            <input 
                                className="input-theme"
                                type="text"
                                value={entreprise}
                                onChange={(e) => setEntreprise(e.target.value)}
                                placeholder="Nom de l'entreprise"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <i className="fas fa-id-card mr-2 text-theme-accent"></i>
                                SIRET
                            </label>
                            <input 
                                className="input-theme"
                                type="text"
                                value={siret}
                                onChange={(e) => setSiret(e.target.value)}
                                placeholder="Numéro SIRET"
                            />
                        </div>
                    </div>
                    <button 
                        onClick={handleCreateUrl}
                        className="btn-theme"
                        disabled={!entreprise || !siret}
                    >
                        <i className="fas fa-link mr-2"></i>
                        Créer URL
                    </button>
                </div>

                {url && (
                    <div className="space-y-8">
                        <div className="card-theme">
                            <h2 className="text-2xl font-bold text-theme-primary mb-6">URL générée</h2>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <input
                                    ref={urlRef}
                                    className="input-theme flex-1 font-mono text-sm"
                                    value={url}
                                    readOnly
                                />
                                <button 
                                    className="btn-theme-secondary"
                                    onClick={handleCopyUrl}
                                >
                                    <i className="fas fa-copy mr-2"></i>
                                    Copier
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-4">
                                <i className="fas fa-info-circle mr-2"></i>
                                Cette URL peut être partagée avec les employés pour qu'ils puissent s'inscrire avec les informations de l'entreprise pré-remplies.
                            </p>
                        </div>

                        <div className="card-theme">
                            <h2 className="text-2xl font-bold text-theme-primary mb-6">Envoyer des invitations par e-mail</h2>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <i className="fas fa-envelope mr-2 text-theme-accent"></i>
                                    Adresses e-mail
                                </label>
                                <input 
                                    className="input-theme"
                                    type="text"
                                    value={emails}
                                    onChange={(e) => setEmails(e.target.value)}
                                    placeholder="email1@example.com, email2@example.com, email3@example.com"
                                />
                                <p className="text-sm text-gray-600 mt-2">
                                    Séparez les adresses e-mail par des virgules
                                </p>
                            </div>
                            <button 
                                onClick={handleSendInvite}
                                className="btn-theme"
                                disabled={!emails.trim()}
                            >
                                <i className="fas fa-paper-plane mr-2"></i>
                                Envoyer les invitations
                            </button>
                        </div>

                        {message && message.startsWith('Erreur') && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {message}
                            </div>
                        )}
                        
                        {showModal && (
                            <Modal contentModal={message} hide={hide} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminCreateSignupPage;